/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { App } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useCoupons } from '@sportninja/common/hooks/useCoupons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import CouponTable from '../components/CouponTable'
import ManageCouponModal from '../modal'

dayjs.extend(utc)

type CouponListPageProps = {
  scheduleId?: string
  organizationId?: string
  currencyCode?: string
  openCreateModalRef?: React.MutableRefObject<(() => void) | null>
}

export default function CouponListPage({
  scheduleId,
  organizationId,
  currencyCode = 'CAD',
  openCreateModalRef,
}: CouponListPageProps) {
  const { message } = App.useApp()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<any>(null)

  const { couponState, refreshCoupons } = useCoupons({
    scheduleId,
    organizationId,
  })

  useEffect(() => {
    refreshCoupons()
  }, [refreshCoupons])

  // Expose open-create to parent via ref
  const openCreate = useCallback(() => {
    setEditingCoupon(null)
    setIsModalOpen(true)
  }, [])

  useEffect(() => {
    if (openCreateModalRef) {
      openCreateModalRef.current = openCreate
    }
    return () => {
      if (openCreateModalRef) {
        openCreateModalRef.current = null
      }
    }
  }, [openCreateModalRef, openCreate])

  const handleEdit = useCallback((record: any) => {
    setEditingCoupon(record)
    setIsModalOpen(true)
  }, [])

  const handleDelete = useCallback(
    async (record: any) => {
      message.success(`Coupon ${record.code} has been deleted.`)
      await refreshCoupons()
    },
    [message, refreshCoupons]
  )

  const handleCopy = useCallback(
    (record: any) => {
      navigator.clipboard.writeText(record.code)
      message.info(`Coupon code ${record.code} copied to clipboard`)
    },
    [message]
  )

  const handleSaveSuccess = useCallback(() => {
    refreshCoupons()
    setIsModalOpen(false)
    setEditingCoupon(null)
  }, [refreshCoupons])

  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      <CouponTable
        coupons={couponState.coupons}
        isLoading={couponState.isLoading}
        currencyCode={currencyCode}
        scheduleId={scheduleId}
        organizationId={organizationId}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCopy={handleCopy}
      />
      <ManageCouponModal
        isFormOpen={isModalOpen}
        setIsFormOpen={setIsModalOpen}
        scheduleId={scheduleId}
        organizationId={organizationId}
        currencyCode={currencyCode}
        editingCoupon={editingCoupon}
        onSaveSuccess={handleSaveSuccess}
      />
    </div>
  )
}
